import { sampleQueryKeys } from '@/config/queryKeys';
import { SampleDetailsPage } from '@/pages/Samples/components/SampleDetailsPage';
import { useOrgId } from '@/pages/Samples/helpers';
import { CONTRACT_BREACH, getSample } from '@/services/samples';
import type { SampleWithNamesPublic } from '@/types';
import { isApiError, isForbiddenError, isUnauthorizedError } from '@/types/errors';
import { FullLoading } from '@components/index';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useQuery } from '@tanstack/react-query';
import { createLazyFileRoute, useNavigate } from '@tanstack/react-router';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function isTerminalRequestError(error: unknown): boolean {
  if (isUnauthorizedError(error)) return true;
  // A malformed response is not transient even though its status is a 5xx.
  if ((error as { code?: string })?.code === CONTRACT_BREACH.code) return true;
  if (!isApiError(error)) return false;
  const status = error.status ?? error.response?.status;
  // 408 and 429 are the two 4xx that explicitly mean "try again".
  return status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429;
}

export const Route = createLazyFileRoute('/samples/$sampleId')({
  component: RouteComponent,
});

function RouteComponent() {
  const { sampleId } = Route.useParams();
  const orgId = useOrgId();
  const { t } = useTranslation('studies');
  const { t: tLogin } = useTranslation('login');
  const navigate = useNavigate();

  const hasOrg = typeof orgId === 'string' && orgId !== '';
  // The loader seeds this entry from the AG Grid list cache for an instant first paint when
  // arriving from the samples list, inheriting that cache's own freshness (see the loader); a
  // deep link or hard refresh finds nothing seeded and falls through to `getSample`.
  const {
    data: sample,
    isPending,
    isError,
    error,
  } = useQuery<SampleWithNamesPublic>({
    queryKey: sampleQueryKeys.detail(orgId ?? '', sampleId),
    queryFn: () => getSample(orgId ?? '', sampleId),
    enabled: hasOrg,
    // No staleTime override: it was Infinity while no endpoint existed, which now pins QC
    // outcomes that flip during processing. Retrying a terminal 4xx only delays the message.
    retry: (failureCount, err) => (isTerminalRequestError(err) ? false : failureCount < 3),
  });

  // 403 only: on a 401 ApiClient's global handler (src/lib/unauthorized-handler.ts) is
  // already navigating to /login, and racing it would strand the user on the wrong page.
  useEffect(() => {
    if (error && isForbiddenError(error)) {
      void navigate({ to: '/unauthorized' });
    }
  }, [error, navigate]);

  // Both redirects above are already in flight, so rendering "not found" here would be
  // misleading and would flash before the navigation lands.
  const errorStatus = isApiError(error) ? (error.status ?? error.response?.status) : undefined;
  const isContractBreach = (error as { code?: string } | null)?.code === CONTRACT_BREACH.code;

  // Logged from an effect, not the render body, so StrictMode's double-invoke and any re-render
  // while the error persists don't double- or repeatedly-emit it.
  useEffect(() => {
    if (isError && sample === undefined && errorStatus !== 404) {
      console.error('[samples/$sampleId] failed to load sample', { sampleId, errorStatus, error });
    }
  }, [isError, sample, sampleId, errorStatus, error]);

  // Only 401/403 have a redirect genuinely in flight; anything else isUnauthorizedError matches
  // (its isUnauthorized flag, its name check) navigates nowhere and would render blank forever.
  if (isError && (errorStatus === 401 || errorStatus === 403)) {
    return null;
  }

  // Gated on the *absence* of cached data: once a sample has rendered once, a later background
  // refetch failure (tab-focus revalidation, the default staleTime lapsing) must not blank a
  // correct page — only a load that never had data to show renders the error state.
  if (isError && sample === undefined) {
    return (
      <Text as="span" data-testid="sample-detail-error">
        {errorStatus === 404
          ? t('samples.details.notFound')
          : isContractBreach
            ? t('samples.details.loadFailedPermanent')
            : t('samples.details.loadFailed')}
      </Text>
    );
  }

  if (!hasOrg || isPending || sample === undefined) {
    return <FullLoading label={tLogin('loading.default')} />;
  }

  return <SampleDetailsPage orgId={orgId} sample={sample} />;
}
